/**
 * `StoreFooter` — shared storefront footer chrome (module 22 editor parity).
 *
 * Presentational + class-free. Renders the store block (+ about), collection
 * links, static quick links, an optional contact column and the platform bar.
 * Session-dependent links (My orders / Sign in) arrive via `quickLinksSlot`;
 * the Flint mark via `flintMark`. Light by default, dark when `config.dark`.
 */
import type { CSSProperties, ReactNode } from "react";
import type { StoreFooterConfig } from "@flint/api-contract/store-home";

interface Collection {
  slug: string;
  name: string;
}

const DEFAULTS: StoreFooterConfig = { showContact: false, dark: false };

const colHeading: CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--muted)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "0.75rem",
};
const linkStyle: CSSProperties = { fontSize: "0.8125rem", color: "var(--muted)", textDecoration: "none" };

export function StoreFooter({
  storeName,
  storeSlug,
  logoUrl,
  collections,
  config,
  contact,
  flintMark,
  quickLinksSlot,
  year,
}: {
  storeName: string;
  storeSlug?: string | undefined;
  /** Theme logo; falls back to an accent initial tile + wordmark. */
  logoUrl?: string | undefined;
  collections?: Collection[] | undefined;
  config?: StoreFooterConfig | undefined;
  contact?: { phone?: string | null; city?: string | null; gstin?: string | null } | undefined;
  flintMark?: ReactNode;
  quickLinksSlot?: ReactNode;
  year: number;
}) {
  const c = config ?? DEFAULTS;
  const darkVars = c.dark
    ? ({
        color: "#f5f0ea",
        "--ink": "#f7f4f0",
        "--muted": "rgba(247,244,240,0.72)",
        "--line": "rgba(247,244,240,0.16)",
      } as CSSProperties)
    : {};

  return (
    <footer
      style={{
        borderTop: "1px solid var(--line)",
        backgroundColor: c.dark ? "#14110f" : "var(--paper)",
        paddingBlock: "2.5rem 1.5rem",
        marginTop: "auto",
        ...darkVars,
      }}
    >
      <div style={{ maxWidth: "var(--s-container, 1120px)", margin: "0 auto", padding: "0 clamp(1rem, 4vw, 2.5rem)" }}>
        <div
          style={{
            display: "grid",
            gap: "2rem",
            marginBottom: "2rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          }}
        >
          {/* Store block */}
          <div>
            {/* Logo: the uploaded mark if the theme has one, else an accent
                initial tile + wordmark — parity with the header (wireframe). */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", marginBottom: "0.75rem" }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${storeName} logo`}
                  style={{ height: "28px", width: "auto", maxWidth: "180px", objectFit: "contain", display: "block" }}
                />
              ) : (
                <>
                  <span
                    aria-hidden
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "var(--r-sm, 6px)",
                      backgroundColor: "var(--accent)",
                      color: "var(--accent-fg)",
                      display: "grid",
                      placeItems: "center",
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: "1rem",
                    }}
                  >
                    {storeName.trim().charAt(0).toUpperCase()}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: "1.125rem",
                      color: "var(--ink)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {storeName}
                  </span>
                </>
              )}
            </div>
            {c.about ? (
              <p style={{ fontSize: "0.8125rem", color: "var(--muted)", lineHeight: 1.55, maxWidth: "32ch", whiteSpace: "pre-wrap" }}>
                {c.about}
              </p>
            ) : null}
          </div>

          {/* Collections */}
          {collections && collections.length > 0 && storeSlug && (
            <div>
              <p style={colHeading}>Collections</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {collections.map((col) => (
                  <a key={col.slug} href={`/s/${storeSlug}/c/${col.slug}`} style={linkStyle}>
                    {col.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Quick links */}
          {storeSlug && (
            <div>
              <p style={colHeading}>Quick links</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                <a href={`/s/${storeSlug}/search`} style={linkStyle}>
                  Search
                </a>
                <a href={`/s/${storeSlug}/cart`} style={linkStyle}>
                  Cart
                </a>
                {quickLinksSlot}
              </div>
            </div>
          )}

          {/* Contact */}
          {c.showContact && contact && (contact.phone || contact.city || contact.gstin) ? (
            <div>
              <p style={colHeading}>Contact</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {contact.phone ? <span style={{ ...linkStyle }}>{contact.phone}</span> : null}
                {contact.city ? <span style={{ ...linkStyle }}>{contact.city}</span> : null}
                {contact.gstin ? <span style={{ ...linkStyle }}>GSTIN {contact.gstin}</span> : null}
              </div>
            </div>
          ) : null}
        </div>

        {/* Platform bar */}
        <div style={{ borderTop: "1px solid var(--line)", paddingTop: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", fontSize: "0.75rem", color: "var(--muted)" }}>
            Powered by {flintMark} <span style={{ fontFamily: "var(--font-display)", fontWeight: 500 }}>Flint</span>
            <span style={{ margin: "0 0.25rem" }}>·</span> {c.tagline ?? "GST-ready · INR · Wholesale packs"}
          </div>
          <p style={{ fontSize: "0.6875rem", color: "var(--muted)" }}>
            © {year} {storeName}
          </p>
        </div>
      </div>
    </footer>
  );
}
